import { ExchangeCode } from "@/lib/market-data/market-data.types";
import {
  CandleFetchParams,
  CandlePoint,
  ExchangeCandleRestAdapter,
} from "../../candle.types";

/**
 * Base URL for the API that serves /market-data/* endpoints. The miniapp's
 * `VITE_API_URL` already includes the `/api` prefix.
 */
const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  "http://localhost:3001/api";

interface ApiCandleResponse {
  success: boolean;
  data?: CandlePoint[];
  error?: string;
}

/**
 * Ask our API to fetch public klines and forward them. Used for exchanges
 * the browser cannot call (KuCoin) and as a backup when a direct request fails.
 */
export function createApiCandlesRestAdapter(
  exchange: ExchangeCode
): ExchangeCandleRestAdapter {
  return {
    async fetchCandles(params: CandleFetchParams): Promise<CandlePoint[]> {
      if (!params.startTime || !params.endTime) {
        throw new Error("api candles: startTime and endTime are required");
      }

      const query = new URLSearchParams({
        interval: params.interval,
        startTime: String(params.startTime),
        endTime: String(params.endTime),
      });
      if (params.limit) query.set("limit", String(params.limit));

      const response = await fetch(
        `${API_BASE_URL}/market-data/candles/${exchange}/${encodeURIComponent(params.symbol)}?${query.toString()}`,
        { signal: AbortSignal.timeout(12_000) }
      );
      if (!response.ok) {
        throw new Error("Price chart is temporarily unavailable");
      }

      const body = (await response.json()) as ApiCandleResponse;
      if (!body.success) {
        throw new Error("Price chart is temporarily unavailable");
      }
      return body.data ?? [];
    },
  };
}
